import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const LICENSE_TYPES = [
  // Cabin operations
  { code: "CABIN_CREW", label: "Kabin Görevlisi", category: "Kabin Operasyon" },
  { code: "CABIN_SENIOR", label: "Kıdemli Kabin Görevlisi / Purser", category: "Kabin Operasyon" },
  { code: "CABIN_SAFETY", label: "Kabin Güvenlik Uzmanı", category: "Kabin Operasyon" },
  // Safety & Emergency
  { code: "SAFETY_OFFICER", label: "Havacılık Güvenlik Yetkilisi", category: "Güvenlik ve Acil Durum" },
  { code: "EMERGENCY_RESPONSE", label: "Acil Müdahale Uzmanı", category: "Güvenlik ve Acil Durum" },
  { code: "FIRE_SAFETY", label: "Yangın Güvenliği ve Kurtarma", category: "Güvenlik ve Acil Durum" },
  // Ground operations
  { code: "GROUND_STAFF", label: "Yer Hizmetleri Görevlisi", category: "Yer Operasyon" },
  { code: "RAMP_AGENT", label: "Apron / Ramp Görevlisi", category: "Yer Operasyon" },
  { code: "LOAD_CONTROL", label: "Yük Kontrol Yetkilisi", category: "Yer Operasyon" },
  { code: "DG_HANDLING", label: "Tehlikeli Madde Uzmanı", category: "Yer Operasyon" },
  { code: "PASSENGER_SERVICE", label: "Yolcu Hizmetleri Görevlisi", category: "Yer Operasyon" },
];

export default function IssueCertificate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    holder_name: "",
    nationality: "",
    date_of_birth: "",
    license_type: "CABIN_CREW",
    issue_date: new Date().toISOString().slice(0, 10),
    expiry_date: "",
    issuing_authority: "International Aviation Verification",
    training_program: "",
    student_id: "",
    notes: "",
  });
  const [modules, setModules] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [issuedCert, setIssuedCert] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/training-programs")
      .then(({ data }) => setPrograms(data.items || []))
      .catch(() => setPrograms([]));
  }, []);

  const onProgramChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, training_program: value }));
    // Auto-fill a module row with the program's default hours, if any
    const match = programs.find((p) => p.name === value);
    if (match && match.default_hours != null && modules.length === 0) {
      setModules([{ name: match.name, hours: String(match.default_hours), completed_date: "" }]);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const addModule = () => setModules((m) => [...m, { name: "", hours: "", completed_date: "" }]);
  const updateModule = (i, k, v) => setModules((m) => m.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeModule = (i) => setModules((m) => m.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const cleanModules = modules
        .filter((m) => m.name.trim() && Number(m.hours) > 0)
        .map((m) => ({
          name: m.name.trim(),
          hours: Number(m.hours),
          completed_date: m.completed_date || null,
        }));
      const { data } = await api.post("/certificates", { ...form, training_modules: cleanModules });
      setIssuedCert(data);
      toast.success(`Sertifika oluşturuldu: ${data.certificate_number}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sertifika oluşturulamadı.");
    } finally {
      setSubmitting(false);
    }
  };

  if (issuedCert) {
    return (
      <div className="p-6 lg:p-10" data-testid="issued-confirmation">
        <div className="max-w-2xl">
          <span className="label-eyebrow">/ Düzenlendi</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
            Sertifika başarıyla oluşturuldu.
          </h1>
          <div className="mt-8 border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-4">
            <CheckCircle size={28} weight="fill" className="text-emerald-700 shrink-0" />
            <div className="flex-1">
              <div className="font-display text-xl font-bold text-emerald-900">
                {issuedCert.holder_name}
              </div>
              <div className="text-sm text-emerald-800 mt-1">
                {LICENSE_TYPES.find((l) => l.code === issuedCert.license_type)?.label}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="label-eyebrow">Sertifika №</div>
                  <div data-testid="issued-cert-number" className="font-mono-plex font-bold text-slate-900 mt-1">
                    {issuedCert.certificate_number}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Geçerlilik</div>
                  <div className="font-mono-plex text-slate-900 mt-1">{issuedCert.expiry_date}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              data-testid="issued-view-pdf"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${issuedCert.certificate_number}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors font-medium text-sm"
            >
              PDF İndir
            </a>
            <a
              data-testid="issued-view-verify"
              href={`/verify/${issuedCert.certificate_number}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 border border-slate-300 hover:bg-slate-50 transition-colors font-medium text-sm"
            >
              Doğrulama Sayfasını Aç
            </a>
            <button
              data-testid="issued-new"
              onClick={() => {
                setIssuedCert(null);
                setForm((f) => ({ ...f, holder_name: "", nationality: "", date_of_birth: "", expiry_date: "", notes: "" }));
              }}
              className="px-5 py-3 border border-slate-300 hover:bg-slate-50 transition-colors font-medium text-sm"
            >
              Yeni Sertifika
            </button>
            <button
              onClick={() => navigate("/admin")}
              className="px-5 py-3 text-slate-700 hover:underline font-medium text-sm"
            >
              Kayıt Defterine Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10" data-testid="issue-form-page">
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        data-testid="back-to-registry"
      >
        <ArrowLeft size={14} /> Kayıt Defterine Dön
      </button>

      <div className="mt-6">
        <span className="label-eyebrow">/ Sertifika Oluştur</span>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
          Yeni havacılık sertifikası
        </h1>
        <p className="text-slate-600 mt-2 max-w-xl">
          Oluşturulan sertifika anında ana kayıt defterine işlenir ve sertifika numarası
          ile QR kod üzerinden herkese açık olarak doğrulanabilir.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mt-10 max-w-4xl bg-white border border-slate-200 p-6 lg:p-8 grid md:grid-cols-2 gap-6">
        <Input label="Ad Soyad" required value={form.holder_name} onChange={update("holder_name")} testid="form-holder-name" placeholder="Resmi kimlikte görüldüğü gibi" />
        <Input label="Öğrenci No (transkript için)" value={form.student_id} onChange={update("student_id")} testid="form-student-id" placeholder="örn. STU-2026-001 (opsiyonel)" />
        <Input label="Uyruk" required value={form.nationality} onChange={update("nationality")} testid="form-nationality" placeholder="örn. Türk, Alman" />
        <Input label="Doğum tarihi" type="date" required value={form.date_of_birth} onChange={update("date_of_birth")} testid="form-dob" />
        <div>
          <label className="label-eyebrow block mb-2">Lisans türü *</label>
          <select
            data-testid="form-license-type"
            value={form.license_type}
            onChange={update("license_type")}
            className="w-full px-4 py-3 border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] text-sm"
          >
            {Array.from(new Set(LICENSE_TYPES.map((l) => l.category))).map((cat) => (
              <optgroup key={cat} label={cat}>
                {LICENSE_TYPES.filter((l) => l.category === cat).map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <Input label="Düzenleme tarihi" type="date" required value={form.issue_date} onChange={update("issue_date")} testid="form-issue-date" />
        <Input label="Geçerlilik tarihi" type="date" required value={form.expiry_date} onChange={update("expiry_date")} testid="form-expiry-date" />
        <Input label="Düzenleyen kurum" value={form.issuing_authority} onChange={update("issuing_authority")} testid="form-authority" className="md:col-span-2" />
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-2">Eğitim programı (sertifika üzerindeki kurs adı)</label>
          {programs.length > 0 ? (
            <>
              <select
                data-testid="form-training-program"
                value={form.training_program}
                onChange={onProgramChange}
                className="w-full px-4 py-3 border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] text-sm"
              >
                <option value="">— Eğitim programı seçin —</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}{p.default_hours != null ? `  (${p.default_hours} saat)` : ""}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-xs text-slate-500">
                Listede aradığınız program yok mu? <Link to="/admin/programs" className="underline text-[#0B2046]">Eğitim Programları sayfasından ekleyin</Link>.
              </div>
            </>
          ) : (
            <>
              <div className="px-4 py-3 border border-amber-200 bg-amber-50 text-sm text-amber-900">
                Henüz tanımlı eğitim programı yok.{" "}
                <Link to="/admin/programs" className="font-semibold underline">
                  Eğitim Programları sayfasından
                </Link>{" "}
                program ekleyin.
              </div>
            </>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-2">Notlar</label>
          <textarea
            data-testid="form-notes"
            value={form.notes}
            onChange={update("notes")}
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] text-sm"
            placeholder="Opsiyonel: ek yetkiler, kısıtlamalar…"
          />
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">Eğitim modülleri</div>
              <p className="text-xs text-slate-500 mt-1">Sertifika PDF'inde kurs adı ve tamamlanan saatler gösterilir.</p>
            </div>
            <button
              type="button"
              onClick={addModule}
              data-testid="form-add-module"
              className="text-sm px-3 py-1.5 border border-slate-300 hover:bg-slate-50"
            >
              + Modül ekle
            </button>
          </div>
          {modules.length === 0 && (
            <div className="text-xs text-slate-400 italic">Henüz eğitim modülü eklenmedi.</div>
          )}
          <div className="space-y-2">
            {modules.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input
                  placeholder="Kurs adı"
                  value={m.name}
                  onChange={(e) => updateModule(i, "name", e.target.value)}
                  data-testid={`form-module-name-${i}`}
                  className="col-span-6 px-3 py-2 border border-slate-300 text-sm bg-white"
                />
                <input
                  type="number"
                  step="0.5"
                  placeholder="Saat"
                  value={m.hours}
                  onChange={(e) => updateModule(i, "hours", e.target.value)}
                  data-testid={`form-module-hours-${i}`}
                  className="col-span-2 px-3 py-2 border border-slate-300 text-sm bg-white font-mono-plex"
                />
                <input
                  type="date"
                  value={m.completed_date}
                  onChange={(e) => updateModule(i, "completed_date", e.target.value)}
                  data-testid={`form-module-date-${i}`}
                  className="col-span-3 px-3 py-2 border border-slate-300 text-sm bg-white"
                />
                <button
                  type="button"
                  onClick={() => removeModule(i)}
                  className="col-span-1 px-2 py-2 text-red-600 hover:bg-red-50 text-sm"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          {modules.length > 0 && (
            <div className="mt-3 text-xs font-mono-plex text-slate-600">
              Toplam saat: {modules.reduce((s, m) => s + (Number(m.hours) || 0), 0).toFixed(1)}
            </div>
          )}
        </div>

        <div className="md:col-span-2 flex items-center justify-between pt-6 border-t border-slate-200">
          <div className="text-xs text-slate-500 font-mono-plex">
            Sertifika № otomatik üretilir.
          </div>
          <button
            type="submit"
            data-testid="form-submit"
            disabled={submitting}
            className="px-6 py-3 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors font-medium disabled:opacity-60"
          >
            {submitting ? "Oluşturuluyor…" : "Sertifika oluştur"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, type = "text", required, value, onChange, testid, placeholder, className = "" }) {
  return (
    <div className={className}>
      <label className="label-eyebrow block mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] text-sm"
      />
    </div>
  );
}
